import { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Container, Input, Label } from "reactstrap";
import { createWaitlistEntry, fetchBranches, getFloors } from "../../../utils/apiCalls";
import { C, reportingGlobalCSS } from "../../reporting/reportingStyles";
import { RCard } from "../../reporting/ReportingShared";
import Breadcrumb from "../../common/breadcrumb";

const fieldStyle = { borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14 };
const labelStyle = { fontSize: 12, fontWeight: 600, color: C.textMed, marginBottom: 4 };

const AddWaitlist = () => {
  const navigate = useNavigate();
  const userType = localStorage.getItem("userType");
  const merchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchant = (userType === "merchant" || userType === "waitlist_staff") ? merchantId : reduxSelectedMerchant;

  const [branches, setBranches] = useState([]);
  const [floors, setFloors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    customerName: "", customerPhone: "", partySize: 1,
    branchId: "", preferredFloorId: "", quotedWaitMinutes: "", notes: "",
  });

  useEffect(() => {
    const load = async () => {
      const response = await fetchBranches({}, false);
      let list = response?.data || [];
      if (selectedMerchant && selectedMerchant !== "all") list = list.filter((b) => String(b.MerchantId) === String(selectedMerchant));
      if (userType === "waitlist_staff") {
        const assigned = JSON.parse(localStorage.getItem("assignedBranchIds") || "[]").map(String);
        list = list.filter((b) => assigned.includes(String(b.id)));
      }
      setBranches(list);
    };
    load();
  }, [selectedMerchant, userType]);

  useEffect(() => {
    if (form.branchId) getFloors({ branchId: form.branchId }).then((data) => setFloors(data || []));
  }, [form.branchId]);

  // Pre-fill estimated wait from the selected branch's default; staff can still override.
  useEffect(() => {
    if (!form.branchId) return;
    const branch = branches.find((b) => String(b.id) === String(form.branchId));
    if (branch?.waitlistCurrentWaitMinutes != null) {
      setForm((prev) => (
        prev.quotedWaitMinutes === "" ? { ...prev, quotedWaitMinutes: String(branch.waitlistCurrentWaitMinutes) } : prev
      ));
    }
  }, [form.branchId, branches]);

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = { ...form, partySize: Number(form.partySize) };
    payload.branchId = Number(payload.branchId);
    if (!payload.preferredFloorId) delete payload.preferredFloorId;
    else payload.preferredFloorId = Number(payload.preferredFloorId);
    if (payload.quotedWaitMinutes) payload.quotedWaitMinutes = Number(payload.quotedWaitMinutes);
    else delete payload.quotedWaitMinutes;
    const result = await createWaitlistEntry(payload);
    setLoading(false);
    if (result) navigate("/table-management/waitlist");
  };

  return (
    <Fragment>
      <style>{reportingGlobalCSS}</style>
      <Breadcrumb title="Add to Waitlist" parent="Table Management" />
      <Container fluid className="rpt-wrap">
        <RCard>
          <form onSubmit={handleSubmit}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div>
                <Label style={labelStyle}>Customer Name *</Label>
                <Input value={form.customerName} onChange={(e) => handleChange("customerName", e.target.value)} required style={fieldStyle} />
              </div>
              <div>
                <Label style={labelStyle}>Phone</Label>
                <Input value={form.customerPhone} onChange={(e) => handleChange("customerPhone", e.target.value)} placeholder="+966..." style={fieldStyle} />
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginTop: 16 }}>
              <div>
                <Label style={labelStyle}>Party Size *</Label>
                <Input type="number" min="1" value={form.partySize} onChange={(e) => handleChange("partySize", e.target.value)} required style={fieldStyle} />
              </div>
              <div>
                <Label style={labelStyle}>Branch *</Label>
                <Input type="select" value={form.branchId} onChange={(e) => handleChange("branchId", e.target.value)} required style={fieldStyle}>
                  <option value="">Select Branch</option>
                  {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </Input>
              </div>
              <div>
                <Label style={labelStyle}>Preferred Floor</Label>
                <Input type="select" value={form.preferredFloorId} onChange={(e) => handleChange("preferredFloorId", e.target.value)} style={fieldStyle}>
                  <option value="">Any floor</option>
                  {floors.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                </Input>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 16, marginTop: 16 }}>
              <div>
                <Label style={labelStyle}>Estimated Wait (min)</Label>
                <Input type="number" value={form.quotedWaitMinutes} onChange={(e) => handleChange("quotedWaitMinutes", e.target.value)} style={fieldStyle} />
              </div>
              <div>
                <Label style={labelStyle}>Notes</Label>
                <Input value={form.notes} onChange={(e) => handleChange("notes", e.target.value)} placeholder="Special requests..." style={fieldStyle} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 24 }}>
              <Button style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMed, fontWeight: 600 }} onClick={() => navigate("/table-management/waitlist")}>Cancel</Button>
              <Button type="submit" style={{ background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, border: "none", borderRadius: 8, fontWeight: 600 }} disabled={loading}>
                {loading ? "Adding..." : "Add to Queue"}
              </Button>
            </div>
          </form>
        </RCard>
      </Container>
    </Fragment>
  );
};

export default AddWaitlist;
